/**
 * Service for managing consequence types in Firestore
 * Provides CRUD operations and caching for consequence types
 */

import {
  collection,
  doc,
  getDocs,
  getDoc,
  addDoc,
  updateDoc,
  deleteDoc,
  serverTimestamp,
  writeBatch
} from 'firebase/firestore';
import { db } from '../firebase.js';

class ConsequenceTypesService {
  constructor() {
    this.collectionName = 'consequenceTypes';
    this.cache = null;
    this.cacheAll = null; // Separate cache for all types including inactive
    this.cacheTimestamp = null;
    this.cacheSchoolId = null;
    this.cacheDuration = 5 * 60 * 1000; // 5 minutes
  }

  /**
   * Get collection reference
   * @param {string} schoolId - School ID (required for hierarchical access)
   */
  getCollectionRef(schoolId) {
    if (!schoolId) {
      throw new Error('schoolId is required for consequenceTypes collection access');
    }
    return collection(db, 'schools', schoolId, this.collectionName);
  }

  /**
   * Check if cache is valid
   */
  isCacheValid(schoolId) {
    if (!this.cache || !this.cacheTimestamp || this.cacheSchoolId !== schoolId) return false;
    return Date.now() - this.cacheTimestamp < this.cacheDuration;
  }

  /**
   * Clear cache
   */
  clearCache() {
    this.cache = null;
    this.cacheAll = null;
    this.cacheTimestamp = null;
    this.cacheSchoolId = null;
  }

  /**
   * Get all consequence types (with caching)
   * @returns {Promise<Array>} Array of consequence type objects
   */
  async getAll(schoolId) {
    if (!schoolId) {
      throw new Error('schoolId is required for getAll consequenceTypes');
    }

    // Return cached data if valid
    if (this.isCacheValid(schoolId)) {
      return this.cache;
    }

    try {
      // Don't use Firestore orderBy to avoid index requirements - sort client-side
      const snapshot = await getDocs(this.getCollectionRef(schoolId));

      const types = snapshot.docs
        .map(doc => {
          const data = doc.data();
          return {
            ...data,
            id: doc.id  // Always use the Firestore document ID, not the data.id field
          };
        })
        .filter(type => type.active !== false && type.retired !== true && type.value); // Only return active, well-formed types

      // Sort client-side by sortOrder (descending - highest first)
      const sorted = types.sort((a, b) => {
        const orderA = a.sortOrder ?? 0;
        const orderB = b.sortOrder ?? 0;
        if (orderA !== orderB) return orderB - orderA; // DESC: higher numbers first
        return (a.label || '').localeCompare(b.label || '');
      });

      // Update cache
      this.cacheAll = null;
      this.cache = sorted;
      this.cacheTimestamp = Date.now();
      this.cacheSchoolId = schoolId;

      return sorted;
    } catch (error) {
      console.error('Error fetching consequence types:', error);
      throw error;
    }
  }

  /**
   * Get all consequence types including inactive ones
   * @returns {Promise<Array>} Array of all consequence type objects
   */
  async getAllIncludingInactive(schoolId) {
    if (!schoolId) {
      throw new Error('schoolId is required for getAllIncludingInactive consequenceTypes');
    }

    // Return cached data if valid
    if (this.isCacheValid(schoolId) && this.cacheAll) {
      return this.cacheAll;
    }

    try {
      // Don't use Firestore orderBy to avoid index requirements - sort client-side
      const snapshot = await getDocs(this.getCollectionRef(schoolId));

      const types = snapshot.docs
        .map(doc => {
          const data = doc.data();
          return {
            ...data,
            id: doc.id  // Always use the Firestore document ID, not the data.id field
          };
        })
        .filter(type => type.value); // Skip malformed docs without a value field

      // Sort client-side by sortOrder (descending - highest first)
      const sorted = types.sort((a, b) => {
        const orderA = a.sortOrder ?? 0;
        const orderB = b.sortOrder ?? 0;
        if (orderA !== orderB) return orderB - orderA; // DESC: higher numbers first
        return (a.label || '').localeCompare(b.label || '');
      });

      // Update BOTH caches
      this.cacheAll = sorted; // All types including inactive
      this.cache = sorted.filter(type => type.active !== false && type.retired !== true); // Active only
      this.cacheTimestamp = Date.now();
      this.cacheSchoolId = schoolId;

      return sorted;
    } catch (error) {
      console.error('Error fetching all consequence types:', error);
      throw error;
    }
  }

  /**
   * Get single consequence type by ID
   * @param {string} id - Document ID
   * @param {string} schoolId - School ID (required for hierarchical access)
   * @returns {Promise<Object|null>} Consequence type object or null
   */
  async getById(id, schoolId) {
    if (!schoolId) {
      throw new Error('schoolId is required for getById consequenceType');
    }

    try {
      const docRef = doc(db, 'schools', schoolId, this.collectionName, id);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        return {
          ...docSnap.data(),
          id: docSnap.id
        };
      }
      return null;
    } catch (error) {
      console.error('Error fetching consequence type:', error);
      throw error;
    }
  }

  /**
   * Get consequence type by value field
   * @param {string} value - The value field to search for
   * @param {string} schoolId - School ID for fetching consequence types
   * @returns {Promise<Object|null>} Consequence type object or null
   */
  async getByValue(value, schoolId) {
    const types = await this.getAll(schoolId);
    return types.find(type => type.value === value) || null;
  }

  /**
   * Get Firestore document ID by value field
   * @param {string} value - The value field to search for
   * @param {string} schoolId - School ID for fetching consequence types
   * @returns {Promise<string|null>} Document ID or null
   */
  async getIdByValue(value, schoolId) {
    const type = await this.getByValue(value, schoolId);
    return type?.id || null;
  }

  /**
   * Create a new consequence type
   * @param {Object} data - Consequence type data
   * @returns {Promise<string>} New document ID
   */
  async create(data, schoolId) {
    if (!schoolId) {
      throw new Error('schoolId is required for create consequenceType');
    }

    try {
      const docData = {
        ...data,
        active: data.active !== undefined ? data.active : true,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };

      const docRef = await addDoc(this.getCollectionRef(schoolId), docData);
      this.clearCache();
      return docRef.id;
    } catch (error) {
      console.error('Error creating consequence type:', error);
      throw error;
    }
  }

  /**
   * Update an existing consequence type
   * @param {string} id - Document ID
   * @param {Object} data - Updated data
   * @returns {Promise<void>}
   */
  async update(id, data) {
    try {
      const docRef = doc(db, this.collectionName, id);
      await updateDoc(docRef, {
        ...data,
        updatedAt: serverTimestamp()
      });
      this.clearCache();
    } catch (error) {
      console.error('Error updating consequence type:', error);
      throw error;
    }
  }

  /**
   * Delete a consequence type
   * @param {string} id - Document ID
   * @returns {Promise<void>}
   */
  async delete(id) {
    try {
      const docRef = doc(db, this.collectionName, id);
      await deleteDoc(docRef);
      this.clearCache();
    } catch (error) {
      console.error('Error deleting consequence type:', error);
      throw error;
    }
  }

  /**
   * Soft delete - mark as inactive
   * @param {string} id - Document ID
   * @returns {Promise<void>}
   */
  async deactivate(id) {
    await this.update(id, { active: false });
  }

  /**
   * Reactivate a consequence type
   * @param {string} id - Document ID
   * @returns {Promise<void>}
   */
  async activate(id) {
    await this.update(id, { active: true });
  }

  /**
   * Update sort order for multiple items
   * @param {Array<{id: string, sortOrder: number}>} items - Items with new sort orders
   * @returns {Promise<void>}
   */
  async updateSortOrder(items) {
    try {
      // Use batch write for better performance and single cache clear
      const batch = writeBatch(db);

      items.forEach(({ id, sortOrder }) => {
        const docRef = doc(db, this.collectionName, id);
        batch.update(docRef, {
          sortOrder,
          updatedAt: serverTimestamp()
        });
      });

      await batch.commit();
      this.clearCache(); // Clear cache ONCE after batch completes
    } catch (error) {
      console.error('Error updating sort order:', error);
      throw error;
    }
  }

  /**
   * Alias for updateSortOrder (for consistency with other managers)
   * @param {Array<{id: string, sortOrder: number}>} items - Items with new sort orders
   * @returns {Promise<void>}
   */
  async updateSortOrders(items) {
    return this.updateSortOrder(items);
  }

  /**
   * Seed initial consequence types from hardcoded data
   * @param {Array} types - Array of consequence type objects
   * @returns {Promise<void>}
   */
  async seedInitialData(types, schoolId) {
    if (!schoolId) {
      throw new Error('schoolId is required for seedInitialData consequenceTypes');
    }

    try {
      const batch = writeBatch(db);

      types.forEach((typeData, index) => {
        const docRef = doc(this.getCollectionRef(schoolId));
        batch.set(docRef, {
          ...typeData,
          sortOrder: typeData.sortOrder !== undefined ? typeData.sortOrder : index,
          active: true,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        });
      });

      await batch.commit();
      this.clearCache();
      console.log(`Successfully seeded ${types.length} consequence types`);
    } catch (error) {
      console.error('Error seeding consequence types:', error);
      throw error;
    }
  }

  /**
   * Get consequence types filtered by severity
   * @param {string} severity - Severity level (low, medium, high, critical)
   * @returns {Promise<Array>} Filtered consequence types
   */
  async getBySeverity(severity, schoolId) {
    const types = await this.getAll(schoolId);
    return types.filter(type => type.severity === severity);
  }

  /**
   * Get consequence types that require action
   * @returns {Promise<Array>} Actionable consequence types
   */
  async getActionableTypes(schoolId) {
    const types = await this.getAll(schoolId);
    return types.filter(type => type.requiresAction === true);
  }

  /**
   * Normalize a consequence type value to its base category
   * Uses the category field if present, otherwise infers from value string
   * @param {string} typeValue - The consequence type value (e.g., "friday-detention")
   * @returns {Promise<string>} Normalized base type (e.g., "detention")
   */
  async normalizeType(typeValue, schoolId) {
    if (!typeValue) return typeValue;

    try {
      const types = await this.getAll(schoolId);
      const matchingType = types.find(t => t.value === typeValue);

      if (matchingType) {
        // Use category field if it exists
        if (matchingType.category) {
          return matchingType.category;
        }

        // Use baseType field if it exists
        if (matchingType.baseType) {
          return matchingType.baseType;
        }
      }

      // Fallback: infer from string patterns
      if (typeValue.includes('detention')) return 'detention';
      if (typeValue.includes('suspension')) return 'suspension';
      if (typeValue === 'demerit' || typeValue === 'demerits') return 'demerit';
      if (typeValue === 'warning' || typeValue === 'warnings') return 'warning';
      if (typeValue === 'mention' || typeValue === 'mentions') return 'mention';

      // Return as-is if no pattern matches
      return typeValue;
    } catch (error) {
      console.error('Error normalizing consequence type:', error);
      // Fallback to string-based inference
      if (typeValue.includes('detention')) return 'detention';
      if (typeValue.includes('suspension')) return 'suspension';
      return typeValue;
    }
  }

  /**
   * Get the base category for multiple types at once (batch operation)
   * @param {Array<string>} typeValues - Array of type values
   * @returns {Promise<Map<string, string>>} Map of original value to normalized type
   */
  async normalizeTypes(typeValues, schoolId) {
    const result = new Map();

    try {
      const types = await this.getAll(schoolId);
      const typeMap = new Map(types.map(t => [t.value, t]));

      for (const typeValue of typeValues) {
        if (!typeValue) {
          result.set(typeValue, typeValue);
          continue;
        }

        const matchingType = typeMap.get(typeValue);

        if (matchingType?.category) {
          result.set(typeValue, matchingType.category);
        } else if (matchingType?.baseType) {
          result.set(typeValue, matchingType.baseType);
        } else {
          // Fallback inference
          if (typeValue.includes('detention')) result.set(typeValue, 'detention');
          else if (typeValue.includes('suspension')) result.set(typeValue, 'suspension');
          else if (typeValue === 'demerit' || typeValue === 'demerits') result.set(typeValue, 'demerit');
          else result.set(typeValue, typeValue);
        }
      }

      return result;
    } catch (error) {
      console.error('Error normalizing consequence types:', error);
      // Fallback: return string-based inference for all
      for (const typeValue of typeValues) {
        if (typeValue?.includes('detention')) result.set(typeValue, 'detention');
        else if (typeValue?.includes('suspension')) result.set(typeValue, 'suspension');
        else result.set(typeValue, typeValue);
      }
      return result;
    }
  }
}

// Export singleton instance
export const consequenceTypesService = new ConsequenceTypesService();
export default consequenceTypesService;
